export function createNestedObjectFromDotKeys(
  pathMap: Record<string, any>,
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [path, value] of Object.entries(pathMap)) {
    const keys = path.split(".");
    let current = result;

    keys.forEach((rawKey, index) => {
      const isLast = index === keys.length - 1;

      // Check if this key is an array index, e.g., "[0]"
      const arrayMatch = rawKey.match(/^\[(\d+)\]$/);
      const isArrayIndex = arrayMatch !== null;
      const key = isArrayIndex ? parseInt(arrayMatch[1], 10) : rawKey;

      if (isLast) {
        if (isArrayIndex) {
          if (!Array.isArray(current)) current = [];
          current[key] = value;
        } else {
          current[key] = value;
        }
      } else {
        let nextKey = keys[index + 1];
        const isNextArray = /^\[\d+\]$/.test(nextKey);

        if (isArrayIndex) {
          if (!Array.isArray(current)) {
            current = [];
          }

          if (!current[key]) {
            current[key] = isNextArray ? [] : {};
          }
          current = current[key];
        } else {
          if (!current[key]) {
            current[key] = isNextArray ? [] : {};
          }
          current = current[key];
        }
      }
    });
  }

  return result;
}

export function getArrayChildren(
  keys: string[],
  basePath: string,
): string[] | undefined {
  // Normalize basePath: remove trailing dot if present
  const normalizedBase = basePath.replace(/\.$/, "");

  // Matches either: a.b[0]. or a.b.[0].  (with or without dot before [0])
  const pattern = new RegExp(`^${normalizedBase}(\\.|\\.)?\\[\\d+\\]\\.`);

  const result = keys.filter((key) => pattern.test(key));
  return result.length > 0 ? result : undefined;
}

/**
 * Converts a nested object to a flat object with dot notation keys
 * This is the opposite of createNestedObjectFromDotKeys
 * @param obj - The nested object to flatten
 * @returns Record with dot notation keys and their values
 *
 * @example
 * flattenObjectToDotKeys({a: {b: {c: "value"}}}) // returns {"a.b.c": "value"}
 * flattenObjectToDotKeys({a: {b: ["x", "y"]}}) // returns {"a.b[0]": "x", "a.b[1]": "y"}
 */
export function flattenObjectToDotKeys(
  obj: any,
  prefix: string = "",
): Record<string, any> {
  const result: Record<string, any> = {};

  if (obj === null || obj === undefined) {
    return result;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const currentKey = prefix ? `${prefix}[${index}]` : `[${index}]`;

      if (typeof item === "object" && item !== null && !Array.isArray(item)) {
        // Recursive call for nested objects in array
        const nestedResult = flattenObjectToDotKeys(item, currentKey);
        Object.assign(result, nestedResult);
      } else if (Array.isArray(item)) {
        // Recursive call for nested arrays
        const nestedResult = flattenObjectToDotKeys(item, currentKey);
        Object.assign(result, nestedResult);
      } else {
        // Primitive value in array
        result[currentKey] = item;
      }
    });
    return result;
  }

  // Handle objects
  if (typeof obj === "object") {
    Object.entries(obj).forEach(([key, value]) => {
      const currentKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === "object" && value !== null) {
        // Recursive call for nested objects or arrays
        const nestedResult = flattenObjectToDotKeys(value, currentKey);
        Object.assign(result, nestedResult);
      } else {
        // Primitive value
        result[currentKey] = value;
      }
    });
    return result;
  }

  // If obj is a primitive value and we have a prefix, use it
  if (prefix) {
    result[prefix] = obj;
  }

  return result;
}
